import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import clsx from "clsx";
import styles from "./linear-progress.module.css";

function clampProgress(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

export type LinearProgressVariant = "accent" | "success" | "danger";

export interface LinearProgressProps {
  value: number;
  variant?: LinearProgressVariant;
  animateOnMount?: boolean;
  durationMs?: number;
  showValue?: boolean;
  className?: string;
  "aria-label"?: string;
}

export function LinearProgress({
  value,
  variant = "accent",
  animateOnMount = true,
  durationMs = 700,
  showValue = false,
  className,
  "aria-label": ariaLabel,
}: LinearProgressProps) {
  const clamped = clampProgress(value);
  const [scale, setScale] = useState(0);
  const [displayedInt, setDisplayedInt] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [motionPreferenceReady, setMotionPreferenceReady] = useState(false);

  const labelRafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    setPrefersReducedMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    );
    setMotionPreferenceReady(true);
  }, []);

  useEffect(() => {
    if (!motionPreferenceReady) return;

    let mountRaf = 0;

    const run = () => {
      const shouldAnimate =
        animateOnMount && !prefersReducedMotion && durationMs > 0;
      
      if (!shouldAnimate) {
        setScale(clamped / 100);
        setDisplayedInt(Math.round(clamped));
        return;
      }

      setScale(clamped / 100);

      if (!showValue) return;

      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / durationMs);
        setDisplayedInt(Math.round(easeOutQuad(t) * clamped));
        if (t < 1) {
          labelRafRef.current = requestAnimationFrame(tick);
        } else {
          labelRafRef.current = null;
        }
      };
      labelRafRef.current = requestAnimationFrame(tick);
    };

    mountRaf = requestAnimationFrame(run);

    return () => {
      cancelAnimationFrame(mountRaf);
      if (labelRafRef.current !== null) {
        cancelAnimationFrame(labelRafRef.current);
        labelRafRef.current = null;
      }
    };
  }, [
    motionPreferenceReady,
    animateOnMount,
    durationMs,
    clamped,
    prefersReducedMotion,
    showValue,
  ]);

  const fillTransition =
    prefersReducedMotion || !animateOnMount || durationMs <= 0
      ? "none"
      : `width ${durationMs}ms cubic-bezier(0.33, 1, 0.68, 1)`;

  const ariaNow = showValue ? displayedInt : Math.round(clamped);

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={ariaNow}
      aria-label={ariaLabel}
      className={clsx(styles.root, className)}
    >
      <div className={styles.barRow}>
        <div className={styles.track}>
          <div
            className={clsx(
              styles.fill,
              variant === "success" && styles.fillSuccess,
              variant === "danger" && styles.fillDanger,
              variant === "accent" && styles.fillAccent,
            )}
            style={{
              width: `${scale * 100}%`,
              transition: fillTransition,
            }}
          />
        </div>
        {showValue ? (
          <span className={clsx("heading-xs", styles.valueLabel)}>
            {displayedInt}%
          </span>
        ) : null}
      </div>
    </div>
  );
}
